import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { handlePutRequest } from '../../service/PutTemplate';
import { endPoints } from '../../Config';
import MachineCatalogFields from './MachineCatalogFields';
import MachineWarehouseDialog, { canManageWarehouse } from './MachineWarehouseDialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateUiText } from '../../utilities/siteTranslations';
import PhotoUploader from '../../components/PhotoUploader';

const AddEditMachine = ({ editable, viewable, onHide, rowRecord, onClose, preferredLanguage: preferredLanguageProp }) => {
    const session = JSON.parse(localStorage.getItem('userDetails') || localStorage.getItem('adminUser') || '{}');
    const canEditSerial = ['admin', 'superAdmin'].includes(session?.data?.adminUser?.role?.uid);
    const [warehouseVisible, setWarehouseVisible] = useState(false);
    const [userOptions, setUserOptions] = useState([]);
    const [loadingBtn, setLoadingBtn] = useState(false);
    const [preferredLanguage, setPreferredLanguage] = useState(preferredLanguageProp || getPreferredLanguage());

    const supportsMachineIdentifiers = /^[^\s]+(?:.*[^\s])?$/;
    const machineIdentifierChars = /^[a-zA-Z0-9.\-_/\\#\s]*$/;
    const machineNameChars = /^[\p{L}\p{N}.\-_/\\#()\s]*$/u;
    const optionalRegex = (regex, message) =>
        Yup.string().test('optional-regex', message, (value) => {
            if (value === null || value === undefined || value === '') return true;
            return regex.test(value);
        });
    const requiredRegex = (regex, requiredMessage, invalidMessage) =>
        Yup.string()
            .required(requiredMessage)
            .test('required-regex', invalidMessage, (value) => {
                if (value === null || value === undefined || value === '') return false;
                return regex.test(value);
            });

    const typeList = [
        {
            id: "snack",
            name: "Snacks",
        },
        {
            id: "coffee",
            name: "Coffee",
        },
        {
            id: "combo",
            name: "Combo",
        },
        {
            id: "drink",
            name: "Drink",
        },
        {
            id: "others",
            name: "Others",
        },
    ];


    // Standardized machine names; this list can be extended from configuration later
    const machineNameList = [
        { id: 'Necta Snakky', name: 'Necta Snakky' },
        { id: 'Necta Solista', name: 'Necta Solista' },
        { id: 'Necta Jazz', name: 'Necta Jazz' },
        { id: 'Necta Tango', name: 'Necta Tango' },
        { id: 'ams', name: 'ams' },
        { id: 'vendo', name: 'vendo' },
        { id: 'crane', name: 'crane' },
        { id: 'Necta Kikkomax', name: 'Necta Kikkomax' },
        { id: 'royal', name: 'royal' },
        { id: 'Necta Samba', name: 'Necta Samba' },
        { id: 'bianchi B200', name: 'bianchi B200' },
        { id: 'Necta Rondo', name: 'Necta Rondo' },
        { id: 'Jetino JL30', name: 'Jetino JL30' },
        { id: 'JL300', name: 'JL300' },
        { id: 'dixie', name: 'dixie' },
        { id: 'Jetino JL28', name: 'Jetino JL28' },
        { id: 'JL26', name: 'JL26' },
        { id: 'Necta Canto', name: 'Necta Canto' },
        { id: 'Necta Opera', name: 'Necta Opera' },
        { id: 'Kikko', name: 'Kikko' },
        { id: 'TCN', name: 'TCN' },
        { id: 'Custom / Other', name: 'Custom / Other' }
    ];
    const machineConditionList = [
        {
            id: 'New',
            name: 'New'
        },
        {
            id: 'Ready to Install',
            name: 'Ready to Install'
        },
        {
            id: 'Needs Repair',
            name: 'Needs Repair'
        }
    ];

    const dispatch = useDispatch();
    const t = (value) => translateUiText(value, preferredLanguage);

    const validationSchema = Yup.object().shape({
        serialNumber: editable && !canEditSerial
            ? Yup.string()
            : Yup.string()
                  .matches(supportsMachineIdentifiers, t('Leading and trailing space is not allowed'))
                  .required(t('This field is required'))
                  .matches(machineIdentifierChars, t('Serial Number contains invalid characters')),
        machine: Yup.string().when('inStockInventory', {
            is: true,
            then: () =>
                optionalRegex(supportsMachineIdentifiers, t('Leading and trailing space is not allowed')).test(
                    'machine-chars',
                    t('Name contains invalid characters'),
                    (value) => !value || machineNameChars.test(value)
                ),
            otherwise: () =>
                editable
                    ? optionalRegex(supportsMachineIdentifiers, t('Leading and trailing space is not allowed')).test(
                          'machine-chars',
                          t('Name contains invalid characters'),
                          (value) => !value || machineNameChars.test(value)
                      )
                    : requiredRegex(supportsMachineIdentifiers, t('This field is required'), t('Leading and trailing space is not allowed')).test(
                          'machine-required-chars',
                          t('Name contains invalid characters'),
                          (value) => !!value && machineNameChars.test(value)
                      )
        }),
        route: Yup.string().nullable(),
        location: optionalRegex(supportsMachineIdentifiers, t('Leading and trailing space is not allowed')),
        user: Yup.string().nullable(),
        model: editable
            ? optionalRegex(supportsMachineIdentifiers, t('Leading and trailing space is not allowed')).test(
                  'model-chars',
                  t('Name contains invalid characters'),
                  (value) => !value || machineNameChars.test(value)
              )
            : requiredRegex(supportsMachineIdentifiers, t('This field is required'), t('Leading and trailing space is not allowed')).test(
                  'model-required-chars',
                  t('Name contains invalid characters'),
                  (value) => !!value && machineNameChars.test(value)
              ),
        type: editable ? Yup.string().nullable() : Yup.string().required(t('This field is required')),
        deviceId: editable
            ? optionalRegex(supportsMachineIdentifiers, t('Leading and trailing space is not allowed')).test(
                  'device-id-chars',
                  t('Device ID contains invalid characters'),
                  (value) => !value || machineIdentifierChars.test(value)
              )
            : requiredRegex(supportsMachineIdentifiers, t('This field is required'), t('Leading and trailing space is not allowed')).test(
                  'device-id-required-chars',
                  t('Device ID contains invalid characters'),
                  (value) => !!value && machineIdentifierChars.test(value)
              )
    });

    const formik = useFormik({
        validationSchema,
        initialValues: {
            machineId: '',
            location: '',
            serialNumber: '',
            user: '',
            route: '',
            routeId: '',
            routeLocationId: '',
            machine: '',
            type: '',
            model: '',
            deviceId: '',
            inStockInventory: false,
            comments: '',
            condition: 'New',
            characteristics: '',
            photo: ''
        },
        onSubmit: async (data) => {
            const user = JSON.parse(localStorage.getItem('adminUser') || '{}');

            if (data.routeId && !data.routeLocationId) return;
            setLoadingBtn(true);

            const addData = {
                location: data?.location ? data?.location : undefined,
                serialNumber: data?.serialNumber,
                user: data?.user || undefined,
                route: data?.route || null,
                routeId: data.routeId || null,
                routeLocationId: data.routeLocationId || null,
                machine: data?.machine,
                type: data?.type,
                model: data?.model,
                deviceId: data?.deviceId,
                condition: data?.condition,
                characteristics: data?.characteristics,
                comments: data?.comments,
                inStockInventory: data?.inStockInventory,
                photo: data?.photo
            };

            if (!editable) {
                const response = await dispatch(handlePostRequest(addData, endPoints.addMachine, true));
                if (response?.status === 201 || response?.status === 200) {
                    formik.resetForm();
                    onHide();
                }
                setLoadingBtn(false);
            } else {
                const machineId = data?.machineId || rowRecord?._id || rowRecord?.id;
                const editData = {
                    ...addData,
                    // Only administrators can change a serial on an existing machine.
                    serialNumber: canEditSerial ? data.serialNumber : undefined,
                    id: machineId
                };
                const response = await dispatch(handlePutRequest(editData, endPoints.updateMachine, true));
                if (response?.status === 200 || response?.status === 201) {
                    formik.resetForm();
                    onHide();
                }
                setLoadingBtn(false);
            }
        }
    });

    const machineOptions = React.useMemo(() => {
        const currentMachine = String(formik?.values?.machine || '').trim();
        const hasCurrentMachine = machineNameList.some((option) => option.id === currentMachine);

        if (!currentMachine || hasCurrentMachine) {
            return machineNameList;
        }

        return [{ id: currentMachine, name: currentMachine }, ...machineNameList];
    }, [formik?.values?.machine, machineNameList]);

    const localizedTypeOptions = React.useMemo(() => typeList.map((option) => ({ ...option, name: t(option.name) })), [preferredLanguage]);
    const localizedConditionOptions = React.useMemo(() => machineConditionList.map((option) => ({ ...option, name: t(option.name) })), [preferredLanguage]);

    const getUserList = async () => {
        const response = await handleGetRequest(endPoints.getallUsers, true);
        setUserOptions(response?.data);
    };

    useEffect(() => {
        getUserList();
    }, []);

    useEffect(() => {
        setPreferredLanguage(preferredLanguageProp || getPreferredLanguage());
    }, [preferredLanguageProp]);

    const getMachineById = async () => {
        setLoadingBtn(true);
        const machineId = rowRecord?._id || rowRecord?.id;
        const response = await handleGetRequest(endPoints.getMachineById(machineId), true);
        if (response) {
            const data = response?.data || {};
            formik.setFieldValue('machineId', data?._id || data?.id || machineId);
            // If in stock, clear and ignore location; otherwise map existing location
            formik.setFieldValue('serialNumber', data?.serialNumber || '');
            formik.setFieldValue('user', data?.user || data?.userId || data?.user?._id || '');
            formik.setFieldValue('route', data?.route || '');
            formik.setFieldValue('routeId', data?.routeId || '');
            formik.setFieldValue('routeLocationId', data?.routeLocationId || '');
            formik.setFieldValue('machine', data?.machine || '');
            formik.setFieldValue('type', data?.type || '');
            formik.setFieldValue('model', data?.model || '');
            formik.setFieldValue('deviceId', data?.deviceId || '');
            formik.setFieldValue('condition', data?.condition || 'New');
            formik.setFieldValue('characteristics', data?.characteristics || '');
            formik.setFieldValue('comments', data?.comments || '');
            formik.setFieldValue('photo', data?.photo || '');
            const inStockInventory = typeof data?.inStockInventory === 'boolean' ? data?.inStockInventory : true;
            formik.setFieldValue('inStockInventory', inStockInventory);
            formik.setFieldValue('location', data?.location || '');
        }
        setLoadingBtn(false);
    };

    useEffect(() => {
        if (editable || viewable) {
            getMachineById();
        }
    }, [rowRecord]);

    return (
        <form onSubmit={formik.handleSubmit}>
            <div className="grid p-fluid">
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="serialNumber">
                            {t('Serial Number')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText
                            id="serialNumber"
                            name="serialNumber"
                            placeholder={t('Enter Serial Number')}
                            value={formik?.values?.serialNumber}
                            maxLength={30}
                            onChange={formik.handleChange}
                            disabled={viewable || (editable && !canEditSerial)}
                        />
                        {formik.touched.serialNumber && formik.errors.serialNumber && <small className="p-error">{formik.errors.serialNumber}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="model">
                            {t('Model')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="model" name="model" placeholder={t('Enter Model')} value={formik?.values?.model} maxLength={30} onChange={formik.handleChange} disabled={viewable} />
                        {formik.touched.model && formik.errors.model && <small className="p-error">{formik.errors.model}</small>}
                    </div>
                </div>{' '}
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="type">
                            {t('Type')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown id="type" name="type" value={formik?.values?.type} options={localizedTypeOptions} optionLabel="name" optionValue="id" onChange={formik.handleChange} placeholder={t('Select Type')} className="w-full" disabled={viewable} />
                        {formik.touched.type && formik.errors.type && <small className="p-error">{formik.errors.type}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="deviceId">
                            {t('Device ID')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="deviceId" name="deviceId" placeholder={t('Enter Device Id')} value={formik?.values?.deviceId} maxLength={11} onChange={formik.handleChange} disabled={viewable} />
                        {formik.touched.deviceId && formik.errors.deviceId && <small className="p-error">{formik.errors.deviceId}</small>}
                    </div>
                </div>
                <MachineCatalogFields routeId={formik.values.routeId} locationId={formik.values.routeLocationId}
                    disabled={viewable || formik.values.inStockInventory}
                    onChange={(routeId, locationId) => { formik.setFieldValue('routeId', routeId); formik.setFieldValue('routeLocationId', locationId); }} />
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label id="machine-name-label" htmlFor="machine-name">
                            {t('Machine')} {!formik?.values?.inStockInventory && <span style={{ color: 'red' }}>*</span>}
                        </label>
                        <Dropdown
                            id="machine"
                            inputId="machine-name"
                            aria-labelledby="machine-name-label"
                            name="machine"
                            editable
                            filter
                            value={formik?.values?.machine}
                            options={machineOptions}
                            optionLabel="name"
                            optionValue="id"
                            onChange={formik.handleChange}
                            placeholder={t('Select Machine Name')}
                            className="w-full"
                            disabled={viewable}
                        />
                        {formik.touched.machine && formik.errors.machine && <small className="p-error">{formik.errors.machine}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="user">
                            {t('Assigned To')}
                        </label>
                        <Dropdown id="user" name="user" value={formik?.values?.user} options={userOptions} optionLabel="firstName" optionValue="_id" onChange={formik.handleChange} placeholder={t('Select User')} className="w-full" disabled={viewable} />
                        {formik.touched.user && formik.errors.user && <small className="p-error">{formik.errors.user}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="condition">
                            {t('Condition')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            id="condition"
                            name="condition"
                            value={formik?.values?.condition}
                            options={localizedConditionOptions}
                            optionLabel="name"
                            optionValue="id"
                            onChange={formik.handleChange}
                            placeholder={t('Select Machine Condition')}
                            className="w-full"
                            disabled={viewable}
                        />
                        {formik.touched.condition && formik.errors.condition && <small className="p-error">{formik.errors.condition}</small>}
                    </div>
                </div>{' '}
                <div className="col-12 md:col-6 mt-5">
                    <div className="field mb-3 flex align-items-center" style={{ gap: '0.5rem' }}>
                        <Checkbox
                            inputId="inStockInventory"
                            name="inStockInventory"
                            checked={formik?.values?.inStockInventory}
                            onChange={(e) => {
                                const checked = e.checked;
                                formik.setFieldValue('inStockInventory', checked);
                                // When marking as in stock, clear and disable location
                                if (checked) {
                                    formik.setFieldValue('routeId', '');
                                    formik.setFieldValue('routeLocationId', '');
                                }
                            }}
                            disabled={viewable}
                        />
                        <label htmlFor="inStockInventory" className="mb-0">
                            {t('In Stock')}
                        </label>
                    </div>
                </div>
                <section className="col-12" aria-labelledby="legacy-machine-fields-title">
                    <div className="surface-50 border-1 surface-border border-round p-3 mb-3">
                        <div id="legacy-machine-fields-title" className="font-semibold mb-2">Datos heredados (deprecados)</div>
                        <p className="text-600 mt-0 mb-3">Se conservan como referencia histórica y se retirarán al finalizar la migración.</p>
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="field mb-3">
                                    <label htmlFor="route">Ruta heredada (deprecada)</label>
                                    <InputText id="route" value={formik.values.route || ''} readOnly className="w-full" placeholder="Sin dato heredado" />
                                    {formik.touched.route && formik.errors.route && <small className="p-error">{formik.errors.route}</small>}
                                </div>
                            </div>
                            <div className="col-12 md:col-6">
                                <div className="field mb-3">
                                    <label htmlFor="location">
                                        Ubicación heredada (deprecada)
                                    </label>
                                    <InputText
                                        id="location"
                                        name="location"
                                        placeholder={t('Enter Location')}
                                        value={formik?.values?.location}
                                        maxLength={800}
                                        onChange={formik.handleChange}
                                        readOnly disabled={viewable || formik?.values?.inStockInventory}
                                    />
                                    {formik.touched.location && formik.errors.location && <small className="p-error">{formik.errors.location}</small>}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <div className="col-12 md:col-12">
                    <div className="field mb-3">
                        <label htmlFor="characteristics">
                            {t('Characteristics')}
                        </label>
                        <InputTextarea id="characteristics" name="characteristics" placeholder={t('Enter Characteristics')} value={formik?.values?.characteristics} maxLength={11} onChange={formik.handleChange} disabled={viewable} />
                        {formik.touched.characteristics && formik.errors.characteristics && <small className="p-error">{formik.errors.characteristics}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-12">
                    <div className="field mb-3">
                        <label htmlFor="comments">
                            {t('Comments')}
                        </label>
                        <InputTextarea
                            id="comments"
                            name="comments"
                            placeholder={t('Enter Comments')}
                            value={formik?.values?.comments}
                            maxLength={500}
                            rows={4}
                            onChange={formik.handleChange}
                            disabled={viewable}
                        />
                        {formik.touched.comments && formik.errors.comments && <small className="p-error">{formik.errors.comments}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-12">
                    <div className="field mb-3">
                        <PhotoUploader
                            label={t('Photo')}
                            value={formik?.values?.photo}
                            onChange={(val) => formik.setFieldValue('photo', val)}
                            helperText={t('Optional')}
                            disabled={viewable}
                            maxUploadBytes={500 * 1024}
                            maxDimension={1400}
                        />
                    </div>
                </div>
            </div>

            <div className="flex justify-content-center mt-4 gap-2">
                {!viewable && (
                    <Button label={editable ? t('Update') : t('Save')} icon={editable ? 'pi pi-pencil' : 'pi pi-save'} type="submit" disabled={loadingBtn || (!!formik.values.routeId && !formik.values.routeLocationId)} className="p-button-success p-button-sm" style={{ minWidth: 90, maxWidth: 120, width: 'auto' }} />
                )}
                {rowRecord?._id && canManageWarehouse(session?.data?.adminUser) && <Button type="button" label="Registro de bodega" icon="pi pi-box" onClick={() => setWarehouseVisible(true)} className="p-button-sm" />}
                {warehouseVisible && <MachineWarehouseDialog machineId={rowRecord._id} onClose={() => setWarehouseVisible(false)} onSaved={() => { setWarehouseVisible(false); onHide(); }} />}
                <Button label={t('Cancel')} icon="pi pi-times" type="button" className="p-button-secondary p-button-sm" style={{ minWidth: 90, maxWidth: 120, width: 'auto' }} onClick={() => onClose()} />
            </div>
        </form>
    );
};

export default AddEditMachine;
